import React, { FC, memo, ReactNode, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker as AntdDatePicker } from 'antd';
import { DatePickerProps as AntdDatePickerProps } from 'antd/es/date-picker';
import s from './index.module.scss';
import classNames from 'classnames';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Toggle } from '../Toggle';
import { DatePickerInput } from '../DatePickerInput';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';

interface DatePickerProps {
  format?: string;
  className?: string;
  onChange?: (date: Dayjs) => void;
  placeholder?: string;
  disabled?: boolean;
  inputReadOnly?: boolean;
  disabledDate?: (date: Dayjs) => boolean;
  showToday?: boolean;
  showPresets?: boolean;
  value?: Nullable<Dayjs>;
  defaultPickerValue?: Nullable<Dayjs>;
  bordered?: boolean;
  requiredMark?: boolean;
  presets?: AntdDatePickerProps['presets'];
  extraFooter?: Nullable<ReactNode>;
  showTime?: boolean;
  startOfDay?: boolean;
  allowClear?: boolean;
}

export const DatePicker: FC<DatePickerProps> = memo((props) => {
  const {
    format,
    className,
    onChange,
    placeholder,
    disabled,
    inputReadOnly,
    disabledDate,
    showToday,
    showPresets,
    value,
    defaultPickerValue,
    requiredMark,
    bordered = true,
    presets,
    extraFooter,
    showTime,
    startOfDay,
    allowClear = true,
  } = props;

  const { t } = useAppTranslation('common');
  const [showTimeState, setShowTimeState] = useState(false);

  const defaultPresets = [
    {
      label: t('Today'),
      value: dayjs(),
    },
    {
      label: t('7 Days ago'),
      value: dayjs().subtract(7, 'day'),
    },
    {
      label: t('2 Weeks ago'),
      value: dayjs().subtract(2, 'week'),
    },
    {
      label: t('1 Month ago'),
      value: dayjs().subtract(1, 'month'),
    },
    {
      label: t('3 Months ago'),
      value: dayjs().subtract(3, 'month'),
    },
    {
      label: t('6 Months ago'),
      value: dayjs().subtract(6, 'month'),
    },
    {
      label: t('1 Year ago'),
      value: dayjs().subtract(1, 'year'),
    },
    {
      label: t('Start of previous month'),
      value: dayjs().subtract(1, 'month').startOf('month'),
    },
    {
      label: t('End of previous month'),
      value: dayjs().subtract(1, 'month').endOf('month'),
    },
  ];

  const handleChange = (date: Dayjs | null): void => {
    if (!date) {
      onChange?.(date as CustomAny);
      return;
    }

    if (!showTimeState) {
      onChange?.(startOfDay ? date.startOf('day') : date.hour(20).minute(0).second(0));
    } else {
      onChange?.(date);
    }
  };

  const isDatePickerInputNeeded = format === CLIENT_DATE_FORMAT || !inputReadOnly;

  return (
    <div className="relative">
      {requiredMark && <div className="text-error text-2xl absolute z-10 top-0 left-1 pointer-events-none">*</div>}
      <AntdDatePicker
        format={format}
        className={classNames(s.datepicker, { [s.borderless]: !bordered }, className)}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        inputReadOnly={inputReadOnly}
        disabledDate={disabledDate}
        value={value || undefined}
        defaultPickerValue={defaultPickerValue}
        presets={showPresets ? presets || defaultPresets : undefined}
        showNow={!showPresets && showToday}
        allowClear={allowClear}
        components={{
          input: isDatePickerInputNeeded ? DatePickerInput : undefined,
        }}
        renderExtraFooter={() => (
          <div className="flex justify-between items-center px-2">
            {showTime && (
              <div className="flex items-center gap-2">
                <Toggle checked={showTimeState} onChange={setShowTimeState} />
                <span>{t('Show time')}</span>
              </div>
            )}
            {extraFooter}
          </div>
        )}
        showTime={showTimeState ? { format: 'HH:mm', minuteStep: 1 } : undefined}
      />
    </div>
  );
});
